<script lang="ts">
    import homepage_search_bg_jpg from '$assets/homepage-search-bg.jpg';
    import homepage_search_bg_webp from '$assets/homepage-search-bg.webp';
    import Picture from '$components/Picture.svelte';
    import { cx } from '$lib/utils/cx.js';
    import { getContainer } from '$components/Context/ContainerContext';
    import {
        gotoSearchUrl,
        type SearchHeaderFormData,
        type Fields,
        isEqualWithoutLookingAtTheShowFilters,
    } from '$components/SearchHeader/SearchHeaderFormData';
    import TypeTabInput from '$components/SearchHeader/TypeTabInput.svelte';
    import WhatWhoInput from '$components/SearchHeader/WhatWhoInput.svelte';
    import WhereInput from '$components/SearchHeader/WhereInput.svelte';
    import MobileTypeInput from '$components/SearchHeader/MobileTypeInput.svelte';
    import MobileSeparator from '$components/SearchHeader/MobileSeparator.svelte';
    import HtmlForm from '$components/Form/HtmlForm.svelte';
    import * as Result from '$lib/utils/result';
    import SubmitButton from '$components/Form/SubmitButton.svelte';
    import { slide } from 'svelte/transition';
    import StackZIndex from '$components/ZIndex/StackZIndex.svelte';
    import * as t from '$trans';
    import type { Form } from '@jmvdevelop/svelte-form';
    import HashtagInput from '$components/SearchHeader/HashtagInput.svelte';
    import SearchHeaderFilters from '$components/SearchHeader/SearchHeaderFilters.svelte';
    import { debounced } from '$lib/utils/store/debounced';
    import NewsfeedFilters from './NewsfeedFilters/NewsfeedFilters.svelte';
    import MobileSearchHeaderFilters from './MobileSearchHeaderFilters.svelte';
    import MobileNewsfeedFilters from './NewsfeedFilters/MobileNewsfeedFilters.svelte';
    import DesktopSeparator from '$components/SearchHeader/DesktopSeparator.svelte';
    import JobFilters from '$components/SearchHeader/JobFilters/JobFilters.svelte';
    import MobileJobFilters from '$components/SearchHeader/JobFilters/MobileJobFilters.svelte';

    const {
        dpapHelper,
        countryHelper,
        urlHelper,
        searchHeaderStore,
        svelteClient,
        browser,
        lang,
    } = getContainer();
    const currentCountry = countryHelper().currentCountry();

    const { displayOnMobile, displayOnDesktop, searchHeaderStyle } = searchHeaderStore();
    $: full = $searchHeaderStyle === 'full';

    export let searchHeaderData: SearchHeaderFormData | null;
    export let form: Form<SearchHeaderFormData, string>;
    export let fields: Fields<SearchHeaderFormData>;

    export let forceIsSubmitting: boolean | null = null;

    async function onSubmit(_form: typeof form): Promise<Result.Result<void, string>> {
        await gotoSearchUrl(form.getState().values, 1, {
            urlHelper: urlHelper(),
            svelteClient,
        });
        return Result.ok(undefined);
    }

    $: type = form.stores().fieldValue(fields.type);
    $: values = form.stores().formValues();
    $: debouncedValues = debounced(values, 500);
    $: withFilters = $type != 'company';

    function changeUrl(_values: SearchHeaderFormData) {
        if (browser() && !full) {
            const values = form.getState().values;
            if (values.type != 'company' && searchHeaderData != null) {
                if (
                    !isEqualWithoutLookingAtTheShowFilters(
                        searchHeaderData,
                        form.getState().values,
                    )
                ) {
                    gotoSearchUrl(values, 1, {
                        urlHelper: urlHelper(),
                        svelteClient,
                    }).catch(console.error);
                }
            }
        }
    }

    $: changeUrl($debouncedValues);
</script>

<StackZIndex let:zIndex>
    <HtmlForm class="" {form} {onSubmit}>
        {#if full}
            <header
                class={cx(
                    'relative',
                    'rounded-b-[24px] t:rounded-b-[16px]',
                    'overflow-hidden',
                )}
            >
                <div class="absolute inset-0 z-0">
                    <Picture
                        class="w-full h-full object-cover bg-gradient-to-t from-red-500 to-green-400"
                        webp={homepage_search_bg_webp}
                        jpg={homepage_search_bg_jpg}
                        alt="image de fond"
                        loading="eager"
                    />
                    <div class="absolute inset-0 bg-search-header"></div>
                </div>

                <div
                    class={cx(
                        'relative',
                        'container',
                        'pt-[118px] px-[20px] pb-[56px]',
                        't:px-0 t:pt-[104px] t:pb-[80px]',
                    )}
                >
                    <h1
                        class={cx(
                            'text-22 t:text-32 leading-relaxed t:leading-normal',
                            'text-white text-center font-black',
                            'm-0 mb-[16px] t:mb-[24px]',
                        )}
                    >
                        Votre réseau
                        <em class="text-brand-yellow not-italic"
                            >d'opportunités professionnelles</em
                        >
                        {dpapHelper().replaceDpAp($currentCountry, `%ap%`)}
                    </h1>

                    <!-- Desktop -->
                    <div class="hidden t:block">
                        <TypeTabInput {form} field={fields.type} />

                        <div
                            class="flex flex-row mt-[16px] rounded-[50px] bg-white shadow-lg"
                        >
                            <WhatWhoInput
                                {form}
                                style="desktop-full"
                                queryField={fields.whatWhoQuery}
                                itemField={fields.whatWhoItem}
                                typeField={fields.type}
                            />

                            {#if $type === 'newsfeed'}
                                <DesktopSeparator />

                                <HashtagInput
                                    {form}
                                    field={fields.hashtag}
                                    style="desktop"
                                />
                            {/if}

                            <DesktopSeparator />

                            <WhereInput
                                {form}
                                style="desktop-full"
                                queryField={fields.whereQuery}
                                itemField={fields.whereItem}
                                showFiltersField={fields.showFilters}
                                withSearch={true}
                                {forceIsSubmitting}
                            />
                        </div>
                    </div>

                    <!-- Mobile -->
                    <div class="block t:hidden">
                        <div
                            class={cx(
                                'bg-white',
                                'min-h-[200px]',
                                'rounded-[24px]',
                                'shadow',
                                'pb-[16px]',
                                'flex flex-col',
                            )}
                        >
                            <div class="flex-1">
                                <MobileTypeInput {form} field={fields.type} />
                                <MobileSeparator />
                                <WhatWhoInput
                                    {form}
                                    style="mobile"
                                    queryField={fields.whatWhoQuery}
                                    itemField={fields.whatWhoItem}
                                    typeField={fields.type}
                                />

                                {#if $type === 'newsfeed'}
                                    <MobileSeparator />

                                    <HashtagInput
                                        {form}
                                        field={fields.hashtag}
                                        style="mobile"
                                    />
                                {/if}

                                <MobileSeparator />
                                <WhereInput
                                    {form}
                                    style="mobile"
                                    queryField={fields.whereQuery}
                                    itemField={fields.whereItem}
                                    showFiltersField={fields.showFilters}
                                />
                            </div>
                            <div class="px-[20px]">
                                <SubmitButton
                                    {form}
                                    class="btn-leo btn-leo-primary btn-lg w-full"
                                    submittingText="Recherche..."
                                    text={t.ui.search({}, $lang)}
                                    {forceIsSubmitting}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </header>
        {:else}
            <!-- Desktop -->
            {#if $displayOnDesktop}
                <header class={cx('bg-gray-100 hidden t:block')} transition:slide>
                    <div
                        style={`--z-index: ${zIndex};`}
                        class={cx(
                            'relative',
                            'container',
                            'h-[100px]',
                            'flex items-center justify-center',
                            'z-[--z-index]',
                        )}
                    >
                        <div class="block w-full">
                            <div class="flex flex-row shadow-leo rounded-[50px] bg-white">
                                <WhatWhoInput
                                    {form}
                                    style="desktop"
                                    queryField={fields.whatWhoQuery}
                                    itemField={fields.whatWhoItem}
                                    typeField={fields.type}
                                    withTypeSelect
                                />

                                {#if $type === 'newsfeed'}
                                    <DesktopSeparator />

                                    <HashtagInput
                                        {form}
                                        field={fields.hashtag}
                                        style="desktop"
                                    />
                                {/if}

                                <DesktopSeparator />

                                <WhereInput
                                    {form}
                                    style="desktop"
                                    queryField={fields.whereQuery}
                                    itemField={fields.whereItem}
                                    showFiltersField={fields.showFilters}
                                    {withFilters}
                                    withSearch={true}
                                    {forceIsSubmitting}
                                />
                            </div>
                        </div>
                    </div>

                    {#if $type === 'pro'}
                        <SearchHeaderFilters
                            {form}
                            showFiltersField={fields.showFilters}
                            proFiltersFields={fields.proFilters}
                        />
                    {:else if $type === 'newsfeed'}
                        <NewsfeedFilters
                            {form}
                            showFiltersField={fields.showFilters}
                            newsfeedFiltersFields={fields.newsfeedFilters}
                        />
                    {:else if $type === 'job'}
                        <JobFilters
                            {form}
                            showFiltersField={fields.showFilters}
                            jobFiltersFields={fields.jobFilters}
                        />
                    {/if}
                </header>
            {/if}

            <!-- Mobile -->
            {#if $displayOnMobile}
                <header
                    style={`--z-index: ${zIndex};`}
                    class={cx(
                        'z-[--z-index]',
                        'bg-gray-100',
                        'w-full overflow-hidden',
                        'flex t:hidden',
                        'items-center justify-center',
                    )}
                >
                    <div
                        class="flex flex-col w-full px-[20px] pt-[30px] pb-[30px] gap-[16px]"
                    >
                        <div
                            class="bg-white rounded-[24px] border border-solid border-gray-300"
                        >
                            <div class="flex">
                                <div class="pl-[20px]">
                                    <MobileTypeInput
                                        {form}
                                        field={fields.type}
                                        variant={'just-icon'}
                                    />
                                </div>

                                <div class="flex-auto">
                                    <WhatWhoInput
                                        {form}
                                        style="mobile"
                                        queryField={fields.whatWhoQuery}
                                        itemField={fields.whatWhoItem}
                                        typeField={fields.type}
                                    />
                                </div>
                            </div>

                            {#if $type === 'newsfeed'}
                                <MobileSeparator />

                                <HashtagInput
                                    {form}
                                    field={fields.hashtag}
                                    style="mobile"
                                />
                            {/if}

                            <MobileSeparator />

                            <WhereInput
                                {form}
                                style="mobile"
                                withSearch={false}
                                queryField={fields.whereQuery}
                                itemField={fields.whereItem}
                                showFiltersField={fields.showFilters}
                                {forceIsSubmitting}
                            />

                            <div
                                class="flex flex-col justify-center gap-[12px] px-[20px] pb-[16px]"
                            >
                                <button
                                    type="button"
                                    class="btn-leo btn-leo-gray-blue btn-xl flex items-center justify-center gap-[10px]"
                                    on:click|preventDefault={() => {
                                        form.dispatch((state) => {
                                            const bool = fields.showFilters.getValue(
                                                state.values,
                                            );
                                            return form
                                                .actions()
                                                .changeValue(!bool, fields.showFilters)(
                                                state,
                                            );
                                        });
                                    }}
                                >
                                    <i class="tnp tnp-filters" aria-hidden={true}></i>
                                    Filtres
                                </button>
                                <SubmitButton
                                    {form}
                                    class="btn-leo btn-leo-primary btn-xl"
                                    text={t.ui.search({}, $lang)}
                                    {forceIsSubmitting}
                                />
                            </div>
                        </div>
                    </div>
                </header>
            {/if}
        {/if}
    </HtmlForm>
</StackZIndex>

{#if $values.showFilters}
    {#if $values.type === 'pro'}
        <MobileSearchHeaderFilters
            {form}
            showFiltersField={fields.showFilters}
            proFiltersFields={fields.proFilters}
        />
    {:else if $values.type === 'newsfeed'}
        <MobileNewsfeedFilters
            {form}
            showFiltersField={fields.showFilters}
            newsfeedFiltersFields={fields.newsfeedFilters}
        />
    {:else if $values.type === 'job'}
        <MobileJobFilters
            {form}
            showFiltersField={fields.showFilters}
            jobFiltersFields={fields.jobFilters}
        />
    {/if}
{/if}
